import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { LogOut, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./HeaderProfileMenu.scss";

interface HeaderProfileMenuProps {
  onLogout: () => void;
}

export const HeaderProfileMenu = ({ onLogout }: HeaderProfileMenuProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updateMenuPosition = useCallback(() => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const gap = 8;
    const menuWidth = 180;

    setMenuStyle({
      position: "fixed",
      top: rect.bottom + gap,
      left: Math.max(8, rect.right - menuWidth),
      width: menuWidth,
      zIndex: 1200,
    });
  }, []);

  useEffect(() => {
    if (!open) return;

    updateMenuPosition();

    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);

    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onEscape);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  return (
    <div className="header-profile-menu" ref={rootRef}>
      <button
        type="button"
        className={`header-profile-trigger${open ? " is-open" : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Меню профиля"
      >
        <User size={20} />
      </button>
      {open &&
        createPortal(
          <div ref={menuRef} className="header-profile-dropdown" style={menuStyle} role="menu">
            <button
              type="button"
              className="header-profile-dropdown-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate("/profile");
              }}
            >
              <User size={16} />
              Профиль
            </button>
            <button
              type="button"
              className="header-profile-dropdown-item header-profile-dropdown-item--danger"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
            >
              <LogOut size={16} />
              Выход
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};
